import { useEffect, useMemo, useRef, useState } from "react";

import EprDao from "@dao/EprDao";
import Marquee from "./Marquee";
import SessionStudent from "@utils/SessionStudent";
import SimpleDialog from "./dialogs/SimpleDialog";
import StudentApplications from "./StudentApplications";
import StudentCarousel from "./StudentCarousel";
import StudentInfoDao from "@dao/StudentInfoDao";
import { translate } from "./translations/Translations";

import { getCountdownDays, getEnglishDate, getEpoch } from "@utils/DateFormatter";
import { useGlobalContext } from "./contextProvider/ContextProvider";
import { gradesOrdinal } from "@const/Grades";
import { getActionControl, isAllowed, isWindowOpen } from "@utils/ActionControlWindows";

import { LocationDto, Student, StudentImage, StudentOe, TimePeriod } from "@/types/GlobalState";

import "@/styles/StudentsContainer.scss";

const resolveSchoolId = (student: Student, allLocationDtos: LocationDto[]): string | number => {
    if (student.schoolId) return student.schoolId;
    if (!student.locationKey) return student.schoolId;
    const locMatch = allLocationDtos.filter(
        (obj) => Number.parseInt(obj.key, 10) === Number.parseInt(student.locationKey as string, 10)
    );
    return locMatch.length > 0 ? locMatch[0].icId[0] : student.schoolId;
};

const getCharterSchoolEntry = (
    student: Student,
    allLocationDtos: LocationDto[]
): { studentNumber: string | number | null; isCharter: boolean } => {
    const schoolId = resolveSchoolId(student, allLocationDtos);
    const studentLocation = allLocationDtos.find((obj: LocationDto) => obj.icId.includes(schoolId.toString()));
    return {
        studentNumber: student.studentNumber,
        isCharter: studentLocation?.locationType === "CHARTER_SCHOOL"
    };
};

const formatStudentEntry = (results: Student[], obj: Student | StudentOe): Student[] => {
    const { schoolChoiceDetailDtos, schoolId, schoolName, studentDto } = obj as StudentOe & Student;
    const dto = studentDto ?? obj;
    const dtoRecord = dto as unknown as Record<string, unknown>;
    dtoRecord.schoolChoiceDetailDtos = schoolChoiceDetailDtos ?? [];
    dtoRecord.schoolName = schoolName ?? dto.schoolName ?? null;
    dtoRecord.studentKey = studentDto
        ? ((obj as StudentOe).studentKey ?? studentDto.studentKey ?? studentDto.key ?? null)
        : null;
    dtoRecord.schoolId = schoolId ?? dto.schoolId ?? null;
    results.push(dto);
    return results;
};

const getStudentGradeName = (s: Student | StudentOe): string =>
    (s as StudentOe).studentDto ? (s as StudentOe).studentDto.gradeName : (s as Student).gradeName;

const getStudentFirstName = (s: Student | StudentOe): string =>
    (s as StudentOe).studentDto ? (s as StudentOe).studentDto.firstName : (s as Student).firstName;

const sortStudents = (a: Student | StudentOe, b: Student | StudentOe): number => {
    const gnA = gradesOrdinal(getStudentGradeName(a));
    const gnB = gradesOrdinal(getStudentGradeName(b));
    if (gnA === gnB) return (getStudentFirstName(a) ?? "").localeCompare(getStudentFirstName(b) ?? "");
    return gnA < gnB ? 1 : -1;
};

const mergeStudentLists = (
    validStudentsIc: Student[],
    validStudentsOe: StudentOe[],
    sessionStudentId: string | null,
    setStudentList: (list: Student[]) => void,
    setSelectedStudent: (s: Student) => void
): void => {
    const oeFromIc = validStudentsIc.reduce(
        (results, dto) => {
            const oeExists = validStudentsOe.filter(
                (obj) => Number.parseInt(obj.studentDto?.studentNumber, 10) === Number.parseInt(dto.studentNumber, 10)
            );
            if (oeExists && oeExists.length > 0) {
                const mergedStudentDto = {
                    ...oeExists[0].studentDto,
                    ...dto // IC student data takes priority for locationKey, gradeName, schoolId, etc.
                };
                const mergedOe = {
                    ...oeExists[0],
                    studentDto: mergedStudentDto,
                    schoolName: dto.schoolName
                };
                results.push(mergedOe);
            } else {
                results.push(dto);
            }
            return results;
        },
        [] as (StudentOe | Student)[]
    );
    oeFromIc.sort(sortStudents);
    const icStudentNumbers = new Set(
        validStudentsIc
            .map((dto) => dto.studentNumber)
            .filter((sn) => sn !== null && sn !== undefined && sn !== "")
            .map(String)
    );
    const filteredOe = validStudentsOe.filter((obj) => {
        const oeStudentNumber = obj.studentDto?.studentNumber;
        if (oeStudentNumber === null || oeStudentNumber === undefined || oeStudentNumber === "") {
            return true;
        }
        return !icStudentNumbers.has(String(oeStudentNumber));
    });
    filteredOe.sort(sortStudents);
    const combined = [...oeFromIc, ...filteredOe];
    const formatted = combined.reduce((acc, entry) => formatStudentEntry(acc, entry), [] as Student[]);
    setStudentList(formatted);
    if (!formatted || formatted.length <= 1) {
        if (formatted?.length === 1) {
            setSelectedStudent(formatted[0]);
        }
        return;
    }
    if (sessionStudentId) {
        const sessionStudentIndex = formatted.findIndex((student) => {
            const studentKeyString = String(student.studentKey);
            const studentNumberString = String(student.studentNumber);
            return studentKeyString === sessionStudentId || studentNumberString === sessionStudentId;
        });
        if (sessionStudentIndex === -1) {
            setSelectedStudent(formatted[0]);
        } else {
            setSelectedStudent(formatted[sessionStudentIndex]);
        }
    } else {
        setSelectedStudent(formatted[0]);
    }
};

interface StudentsContainerProps {
    loader: boolean;
    setLoader?: (loader: boolean) => void;
}

/**
 * Display the student carousel, the selected student marquee and possibly welcome text
 * @name StudentsContainer
 */
const StudentsContainer = ({ loader, setLoader }: StudentsContainerProps): React.JSX.Element => {
    const { dispatch, state } = useGlobalContext();
    const {
        allLocationDtos,
        oeActionControls,
        oeActiveDistrictWindow,
        oeDistrictWindows,
        oeStudentEligibility,
        guardianState,
        lang,
        schoolYearDto,
        oeSchoolYearRange,
        studentImageList,
        studentsCharterSchool,
        studentsHomeSchooled,
        studentsFlag,
        studentsIc,
        studentsOe,
        timePeriods,
        token
    } = state || {};
    const typedStudentsIc = useMemo(() => studentsIc ?? [], [studentsIc]);

    const [addAllowed, setAddAllowed] = useState(false);
    const [expand, setExpand] = useState(false);
    const [schoolYearRange, setSchoolYearRange] = useState("2027 - 2028");
    const [selectedStudent, setSelectedStudent] = useState<StudentOe | Student | null>(null);
    const [studentsComplete, setStudentsComplete] = useState(false);
    const [studentList, setStudentList] = useState<(StudentOe | Student)[] | null>(null);

    const globalNoStudent1 = translate("Global_No_Student_1", lang ?? "");
    const globalNoStudent2 = translate("Global_No_Student_2", lang ?? "");
    const globalNoStudentExternal = translate("Global_No_Student_External", lang ?? "");
    const globalNoStudentGuardian = translate("Global_No_Student_Guardian", lang ?? "");

    const prActionControl = useMemo(() => {
        if (oeActionControls && oeActiveDistrictWindow) {
            return getActionControl("PARENT_REQUEST", oeActionControls, oeActiveDistrictWindow);
        }
        return null;
    }, [oeActionControls, oeActiveDistrictWindow]);

    const prStartDate = prActionControl?.startDate || "2026-11-01 08:00:00";
    const formattedStartDate = getEnglishDate(prStartDate);
    const countdownDays = getCountdownDays(prStartDate);
    // const schoolYearName = oeSchoolYearRange?.name || "2026-2027";

    const guardianMessage = useMemo(() => {
        if (addAllowed) {
            return (
                <div className="guardian-no-student-details">
                    <div className="info-section">
                        <p>{globalNoStudentGuardian}</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="guardian-no-student-details">
                <div className="info-section">
                    <p>
                        {translate("OE_Guardian_No_Students_IT_Help", lang)}
                        <strong>303-387-0001</strong>.
                    </p>
                </div>
                <div className="oe-notice-card">
                    <div className="oe-notice-header">{translate("OE_Notice_Header", lang)}</div>
                    <p>
                        {translate("OE_Notice_Begins_Pre", lang)}
                        <strong>{schoolYearRange}</strong>
                        {translate("OE_Notice_Begins_Mid", lang)}
                        <strong>{formattedStartDate}</strong>.
                    </p>
                    {countdownDays !== null && (
                        <div className="oe-countdown-pill">
                            <span>⏳</span> {translate("OE_Countdown_Opens_In", lang)}
                            {countdownDays}{" "}
                            {Number(countdownDays) === 1
                                ? translate("OE_Countdown_Day", lang)
                                : translate("OE_Countdown_Days", lang)}
                        </div>
                    )}
                    <p className="subtext">{translate("OE_Notice_Subtext", lang)}</p>
                </div>
            </div>
        );
    }, [addAllowed, countdownDays, formattedStartDate, globalNoStudentGuardian, lang, schoolYearRange]);

    const externalMessage = useMemo(
        () => (
            <div className="guardian-no-student-details">
                <div className="info-section">
                    <p>{globalNoStudentExternal}</p>
                </div>
            </div>
        ),
        [globalNoStudentExternal]
    );

    useEffect(() => {
        setExpand(false);
    }, [selectedStudent]);

    useEffect(() => {
        if (oeSchoolYearRange) {
            const [start, end] = oeSchoolYearRange.name.split(" - ");
            setSchoolYearRange(`${Number(start) + 1} - ${Number(end) + 1}`);
        }
    }, [oeSchoolYearRange]);

    /**
     * If there is a cookie present, set the sessionStudent and remove the cookie
     */
    SessionStudent(null, "sessionStudent");

    /**
     * Determine if the user is allowed to add a student
     */
    useEffect(() => {
        if (!oeActionControls || !oeActiveDistrictWindow || !oeDistrictWindows) {
            setAddAllowed(false);
            return;
        }
        if (
            isAllowed("PARENT_REQUEST", oeActionControls, oeActiveDistrictWindow, oeDistrictWindows) &&
            isWindowOpen(oeActiveDistrictWindow.startDate, oeActiveDistrictWindow.endDate)
        ) {
            setAddAllowed(true);
        } else {
            setAddAllowed(false);
        }
    }, [oeActionControls, oeActiveDistrictWindow, oeDistrictWindows]);

    /**
     * Get a list of IC student image blobs
     */
    useEffect(() => {
        if (!schoolYearDto || !token || typedStudentsIc.length === 0) {
            return;
        }

        /**
         * @name fetchStudentImages
         * @description Get a list of IC student image blobs
         * @async
         */
        const fetchStudentImages = async (): Promise<void> => {
            try {
                const imagePromises = typedStudentsIc.map(async (student) => {
                    const cached = studentImageList?.find((img) => img.studentNumber === student.studentNumber);
                    if (cached) return cached;
                    try {
                        const options = {
                            action: "studentImageRead" as const,
                            ignoreError: true,
                            schoolYearKey: schoolYearDto.key,
                            studentNumber: student.studentNumber as string | number | null | undefined
                        };
                        const response = await StudentInfoDao(options);
                        if (response?.data) {
                            const urlCreator = globalThis.URL || globalThis.webkitURL;
                            return {
                                studentNumber: student.studentNumber,
                                imageUrl: urlCreator.createObjectURL(
                                    new Blob([response.data as BlobPart], { type: "image/jpeg" })
                                )
                            };
                        }
                        return undefined;
                    } catch (error) {
                        console.error("Unable to retrieve Student Image", error);
                        return {
                            studentNumber: student.studentNumber,
                            imageUrl: null
                        };
                    }
                });

                // Resolve all promises and filter out null results
                const studentImages = (await Promise.all(imagePromises)).filter(Boolean);
                // Dispatch only after all images have been fetched
                dispatch({
                    type: "StudentImageList",
                    studentImageList: studentImages as StudentImage[]
                });
            } catch (error) {
                console.error(`Unable to retrieve Student Images for school year ${schoolYearDto.key}:\n`, error);
            }
        };

        if (guardianState && studentImageList?.length !== typedStudentsIc.length) {
            void fetchStudentImages();
        }
    }, [dispatch, guardianState, schoolYearDto, studentImageList, token, typedStudentsIc]);

    /**
     * Get a list of home schooled students
     */
    useEffect(() => {
        if (studentsHomeSchooled || !allLocationDtos || !token || typedStudentsIc.length === 0) {
            return;
        }

        /**
         * @name fetchHomeSchooledStudents
         * @description Determine if a student is home schooled
         * @async
         */
        const fetchHomeSchooledStudents = async (): Promise<void> => {
            try {
                const homeSchoolPromises = typedStudentsIc.map(async (student) => {
                    const schoolId = resolveSchoolId(student, allLocationDtos);
                    const options = {
                        action: "studentInfoStudentRead" as const,
                        personId: student.key,
                        schoolId
                    };
                    try {
                        const response = await StudentInfoDao(options);
                        if (response?.data) {
                            const { payload } = response.data as { payload?: boolean | string };
                            return {
                                studentNumber: student.studentNumber,
                                isHomeSchooled: !["false", false].includes(payload as boolean | string)
                            };
                        }
                    } catch (error) {
                        console.error("Unable to retrieve home schooled status for student", error);
                    }
                    return undefined;
                });
                // Resolve all promises
                const studentsHome = await Promise.all(homeSchoolPromises);
                dispatch({
                    type: "StudentsHomeSchooled",
                    studentsHomeSchooled: studentsHome
                });
            } catch (error) {
                console.error(`Unable to retrieve home schooled status for IC students:\n`, error);
            }
        };

        void fetchHomeSchooledStudents();
    }, [allLocationDtos, dispatch, studentsHomeSchooled, token, typedStudentsIc]);

    /**
     * Get a list of home schooled students
     */
    useEffect(() => {
        if (studentsCharterSchool || !allLocationDtos || typedStudentsIc.length === 0) {
            return;
        }

        /**
         * @name getCharterSchoolStudents
         * @description Determine if a student is home schooled
         */
        const getCharterSchoolStudents = (): void => {
            try {
                const charterSchoolStudents = typedStudentsIc.map((student) =>
                    getCharterSchoolEntry(student, allLocationDtos)
                );

                dispatch({
                    type: "StudentsCharterSchool",
                    studentsCharterSchool: charterSchoolStudents
                });
            } catch (error) {
                console.error(`Unable to retrieve charter school status for IC students:\n`, error);
            }
        };

        getCharterSchoolStudents();
    }, [allLocationDtos, dispatch, studentsCharterSchool, typedStudentsIc]);

    const lastMergeKey = useRef<string | null>(null);

    /**
     * Assemble a list of IC and OE students combined into a common dto
     */
    useEffect(() => {
        const validStudentsIc = typedStudentsIc;
        const validStudentsOe = studentsOe || [];
        const mergeKey = `${validStudentsIc.length}-${validStudentsOe.length}`;

        if (lastMergeKey.current === mergeKey) return;
        lastMergeKey.current = mergeKey;

        if (validStudentsIc.length > 0 || validStudentsOe.length > 0) {
            const sessionStudentId = sessionStorage.getItem("sessionStudent");
            mergeStudentLists(validStudentsIc, validStudentsOe, sessionStudentId, setStudentList, setSelectedStudent);
        }
    }, [studentsOe, typedStudentsIc]);

    /**
     * Get a list of time periods for the school year
     */
    useEffect(() => {
        if (timePeriods || !guardianState || !schoolYearDto || !token) {
            return;
        }

        /**
         * @name fetchTimePeriods
         * @description Get a list of all time periods for the school year
         * @async
         */
        const fetchTimePeriods = async (): Promise<void> => {
            try {
                const options = {
                    action: "timePeriodsBySchoolYearRead",
                    schoolYearKey: schoolYearDto.key,
                    token
                };
                const response = await EprDao(options);
                if (response?.data) {
                    const { payload } = response.data as { payload?: TimePeriod[] };
                    dispatch({
                        type: "TimePeriods",
                        timePeriods: payload as TimePeriod[]
                    });
                }
            } catch (error) {
                console.error("Unable to retrieve time periods:\n", error);
            }
        };

        void fetchTimePeriods();
    }, [dispatch, guardianState, schoolYearDto, timePeriods, token]);

    /**
     * Manages the main loader state.
     */
    useEffect(() => {
        if (studentsFlag) {
            const { IC, OE } = studentsFlag;
            if (OE && IC) {
                setStudentsComplete(true);
                // If both student lists have been fetched and are empty, it's safe to turn off the loader.
                // Otherwise, the loader will be managed by the StudentApplications component
                // once a student is selected and applications begin to load.
                if ((studentsIc ?? []).length === 0 && (studentsOe ?? []).length === 0) {
                    setLoader?.(false);
                }
            }
        }
    }, [studentsFlag, studentsIc, studentsOe, setLoader]);

    /**
     * Is the student's age 5 or older by Oct 2 of the next school year?
     */
    useEffect(() => {
        if (oeStudentEligibility || !oeSchoolYearRange || !studentsIc || !token) {
            return;
        }

        const getStudentsEligibity = (): void => {
            try {
                const studentsEligibility = typedStudentsIc.map((student) => {
                    const { birthdate } = student;
                    const [year, month, day] = birthdate.split("-");
                    if (day && month && oeSchoolYearRange && year) {
                        // Age 5 in milliseconds
                        const baseAge = 60 * 60 * 24 * 365.242199 * 5 * 1000;
                        // Grab the end year of the OE schoolYear
                        const endYear = oeSchoolYearRange.name?.slice(-4);
                        // convert to milliseconds
                        const endRange = getEpoch(`${endYear}-10-02`);
                        // convert the student's birthday to milliseconds
                        const ageEpoch = getEpoch(birthdate);
                        // difference between 10/02/<endYear> and student's birthday
                        const timeDiff = (endRange ?? 0) - (ageEpoch ?? 0);

                        return {
                            studentNumber: student.studentNumber,
                            eligible: (timeDiff ?? 0) >= (baseAge ?? 0)
                        };
                    }
                    return { studentNumber: student.studentNumber, eligible: false };
                });

                dispatch({
                    type: "OeStudentEligibility",
                    oeStudentEligibility: studentsEligibility
                });
            } catch (error) {
                console.error(error);
            }
        };

        getStudentsEligibity();
    }, [dispatch, oeSchoolYearRange, oeStudentEligibility, studentsIc, token, typedStudentsIc]);

    return (
        <div className="students-container">
            {(addAllowed || selectedStudent) && (
                <>
                    <div className="carousel-container">
                        <StudentCarousel
                            addAllowed={addAllowed}
                            setExpand={setExpand}
                            setSelectedStudent={setSelectedStudent}
                            studentImageList={studentImageList || []}
                            studentList={studentList || []}
                        />
                    </div>
                    <hr />
                </>
            )}
            {selectedStudent && (
                <div className="student-info animate-height-from-fixed">
                    <div className="marquee">
                        <Marquee student={selectedStudent} studentImageList={studentImageList ?? []} />
                    </div>
                    <div className="student-applications">
                        {studentsComplete && (
                            <StudentApplications
                                expand={expand}
                                isGuardian={guardianState ?? false}
                                lang={lang ?? ""}
                                loader={loader}
                                setExpand={setExpand}
                                setLoader={setLoader}
                                student={selectedStudent as Student}
                            />
                        )}
                    </div>
                </div>
            )}
            {!loader && !selectedStudent && (
                <SimpleDialog
                    message1={globalNoStudent1}
                    message2={globalNoStudent2}
                    message3={guardianState ? guardianMessage : externalMessage}
                />
            )}
        </div>
    );
};

export default StudentsContainer;
